import React from "react";
import { Tag, Popover, Segmented } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { machineryApis } from "../../../api/MachineryNorms";
import { deploymentNormApis } from "../../../api/MachineryEquipment";
import {
  LIVE_MACHINERY_HEADS,
  DEFAULT_MACHINERY_HEAD,
} from "../machinery-norms/machineryHeads";

// ONE modal, TWO flows, switched by the Head picker.
//
// Machinery heads (e.g. Mucking) — bulk "Apply Machinery" upload: rows are
// {TaskID, Activity Name, Norm Code, Description, OverrideQty}. Project-scoped:
// the current project's id is sent in the body so the TaskID (task sequence
// number) resolves uniquely. The norm is matched WITHIN the task's activity
// (strict activity rule); Description is only needed when the same Norm Code
// exists twice under that activity with different descriptions (e.g. B3.1.1
// Class II vs Class III).
//
// Equipment — bulk "Apply Deployment Norms" upload: rows are {TaskID, Norm
// Code, OverrideQty}. The Norm Code must be an ACTIVE deployment norm of this
// SAME project — org-master norms must be duplicated into the project first
// (Norms → Machinery → Deployment Norms → Duplicate to Project). Only the
// QUANTITY freezes at commit (round2(OverrideQty ?? task work quantity); 0
// valid); equipment lines, hours and amounts stay LIVE-derived from the
// version-in-force + the equipment hourly rates.
//
// Both flows are thin wrappers over the shared staged-upload modal (upload →
// validating → preview → committing → done) and per-row resilient — valid rows
// apply, invalid rows are reported. Repeat a TaskID across rows to apply
// several norms to one task.

// "EQUIPMENT" is a LOCAL, display-only segment value of this modal's Head
// picker — deployment norms are a SEPARATE ENTITY (machinery-equipment: own
// table, own APIs), NOT a machinery_norms head. Do not add it to
// machineryHeads.ts, and never send it to the backend as a `category` (the
// deployment buildBody deliberately has no category field).
const EQUIPMENT_SEGMENT = "EQUIPMENT";

// ------------------------------------------------------------ machinery flow

const generateApplyMachineryTemplate = () => {
  const headers = ["TaskID", "Activity Name", "Norm Code", "Description", "OverrideQty"];
  const sampleRows = [
    ["T-0001", "Tunnel Excavation", "B2.1", "", ""],
    ["T-0001", "Tunnel Excavation", "B3.1.1", "Class II", ""],
    ["T-0002", "Mucking & Hauling", "B3.1.1", "Class III", "120"],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 18 }, { wch: 30 }, { wch: 16 }, { wch: 28 }, { wch: 14 }];

  const instructions = [
    ["Apply Machinery Upload — Instructions"],
    [""],
    ["TaskID", "Required. The task's number (sequence id) shown in the app, within THIS project. Repeat the TaskID on multiple rows to apply several machinery norms to the same task."],
    ["Activity Name", "Required. The task's activity name as shown in the app. It is cross-checked against the TaskID's own activity — a mismatch rejects the row (the norm is always matched within the task's activity, this column never redirects the lookup)."],
    ["Norm Code", "Required. The machinery norm's code. It is matched within the TASK'S ACTIVITY, under the HEAD selected in the upload dialog (e.g. Mucking) — a code that exists only under a different activity or head is rejected."],
    ["Description", "Optional. Add it ONLY when the same Norm Code appears under the task's activity with multiple descriptions (e.g. B3.1.1 Class II vs Class III) — it picks which one to apply. Leave blank otherwise."],
    ["OverrideQty", "Optional. Replaces the task's work quantity as the basis for that row. Leave blank to use the task work quantity. 0 is allowed; negatives are rejected."],
    [""],
    ["Note", "Applying a machinery norm records it as the task's machinery requirement (quantity = task work quantity or the override, priced at the norm rate). A norm can be applied to a task only ONCE — a row whose task already has that norm is rejected (remove it with a reason first). Invalid rows (unknown task/norm, wrong activity, ambiguous description, inactive, already applied, duplicate in file, negative override) are skipped and listed in the preview; valid rows still apply."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 16 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "ApplyMachinery");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "apply-machinery-upload-template.xlsx");
};

const rowErrors = (record: any): string[] => {
  const raw = record?.Error ?? record?.error;
  return Array.isArray(raw) ? raw : raw ? [raw] : [];
};

const machineryPreviewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      const action = record?.__action ?? record?.status;
      return action === "Applied" ? (
        <Tag color="blue">Applied</Tag>
      ) : (
        <Tag color="green">Valid</Tag>
      );
    },
  },
  {
    title: "Task ID",
    key: "TaskID",
    width: 120,
    render: (_: any, record: any) =>
      record?.taskRef ?? record?.TaskID ?? record?.taskId ?? "",
  },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 200, ellipsis: true },
  {
    title: "Activity",
    key: "activityName",
    width: 180,
    ellipsis: true,
    render: (_: any, record: any) => record?.activityName ?? "",
  },
  {
    title: "Norm Code",
    key: "NormCode",
    width: 120,
    render: (_: any, record: any) =>
      record?.normCode ?? record?.NormCode ?? record?.["Norm Code"] ?? "",
  },
  {
    title: "Description",
    key: "Description",
    width: 200,
    ellipsis: true,
    render: (_: any, record: any) =>
      record?.resolvedDescription ??
      record?.Description ??
      record?.description ??
      "",
  },
  {
    title: "Head",
    key: "head",
    width: 120,
    // Head filter: only heads that actually accept data. (This previously used
    // MACHINERY_HEADS, which then included eight heads that could never have
    // rows — a filter that always returned nothing.) Unknown heads coming back
    // from the server are still displayed, just not filterable.
    filters: LIVE_MACHINERY_HEADS.map((h) => ({ text: h.label, value: h.key })),
    onFilter: (value: any, record: any) =>
      ((record?.head ?? record?.category ?? "") as string).toUpperCase() ===
      String(value).toUpperCase(),
    render: (_: any, record: any) => record?.head ?? record?.category ?? "",
  },
  {
    title: "Qty",
    key: "qty",
    width: 110,
    render: (_: any, record: any) =>
      record?.appliedQty ?? record?.qty ?? record?.OverrideQty ?? "",
  },
  {
    title: "Rate",
    key: "rate",
    width: 110,
    render: (_: any, record: any) => record?.rate ?? "",
  },
  {
    title: "Amount",
    key: "amount",
    width: 120,
    render: (_: any, record: any) => record?.amount ?? "",
  },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

// ------------------------------------------------- deployment (Equipment) flow

const generateApplyDeploymentTemplate = () => {
  const headers = ["TaskID", "Norm Code", "OverrideQty"];
  const sampleRows = [
    ["T-0001", "B3.1.1", ""],
    ["T-0002", "B3.1.1", "120"],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 14 }];

  const instructions = [
    ["Apply Equipment Norms Upload — Instructions"],
    [""],
    ["TaskID", "Required. The task's number (sequence id) shown in the app, within THIS project — tasks from other projects are rejected. Repeat the TaskID on multiple rows to apply several equipment norms to the same task."],
    ["Norm Code", "Required. The equipment norm's code. It must be an ACTIVE equipment norm of this SAME project — org-master norms are NOT applicable directly; duplicate them into the project first (Norms → Machinery → Equipment Norms → Duplicate to Project)."],
    ["OverrideQty", "Optional. Replaces the task's work quantity as the frozen basis for that row. Leave blank to use the task work quantity. 0 is allowed; negatives are rejected."],
    [""],
    ["Note", "Only the QUANTITY freezes at commit. The norm's equipment lines, hours and amounts stay LIVE-derived from the version in force and the equipment hourly rates — re-norming or re-pricing reaches applied tasks automatically. Invalid rows (unknown task/norm, wrong project, inactive norm, already applied, duplicate in file, negative override) are skipped and listed in the preview; valid rows still apply."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 16 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "ApplyEquipment");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "apply-equipment-norms-template.xlsx");
};

const deploymentPreviewColumns: any[] = [
  {
    title: "Row",
    key: "excelRow",
    width: 70,
    fixed: "left",
    render: (_: any, record: any) => record?.excelRow ?? "",
  },
  {
    title: "TaskID",
    key: "TaskID",
    width: 120,
    render: (_: any, record: any) =>
      record?.taskRef ?? record?.TaskID ?? record?.taskId ?? "",
  },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 220, ellipsis: true },
  {
    title: "Norm Code",
    key: "NormCode",
    width: 120,
    render: (_: any, record: any) =>
      record?.normCode ?? record?.NormCode ?? record?.["Norm Code"] ?? "",
  },
  {
    title: "Description",
    key: "Description",
    width: 220,
    ellipsis: true,
    render: (_: any, record: any) => record?.description ?? "",
  },
  {
    // The FROZEN applied quantity (OverrideQty ?? task work quantity).
    // Hours/amounts are deliberately absent — they stay live-derived.
    title: "Qty",
    key: "qty",
    width: 110,
    render: (_: any, record: any) =>
      record?.appliedQuantity ?? record?.OverrideQty ?? "",
  },
  {
    title: "Action",
    key: "action",
    width: 100,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return <Tag color="green">{record?.__action ?? "Apply"}</Tag>;
    },
  },
  {
    title: "Error",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

// Every non-error row is the same action ('Apply'), so a Valid bucket adds
// nothing — just All / Errors.
const deploymentBucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => rowErrors(row).length);
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

// ------------------------------------------------------------------ component

const ApplyMachineryNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  // Head the upload runs under — mirrors the master upload modal's picker;
  // re-synced to the default on every open. A real machinery head (e.g.
  // MUCKING) runs the machinery_norms flow and is sent as `category`; the
  // EQUIPMENT segment swaps the whole config to the deployment-norm flow
  // (never sent as a category).
  const [head, setHead] = React.useState<string>(DEFAULT_MACHINERY_HEAD);
  React.useEffect(() => {
    if (open) setHead(DEFAULT_MACHINERY_HEAD);
  }, [open]);

  const config: ExcelUploadTypeConfig = React.useMemo(() => {
    // The picker lives in BOTH variants' introSection so the user can always
    // switch flows. With the Equipment segment there are always ≥2 options,
    // so the old one-option-Segmented-is-noise fallback (a plain head Tag,
    // gated on hasMultipleMachineryHeads) is gone.
    const headPicker = (
      <div className="mt-3">
        <label className="form-label mb-1">Head</label>
        <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
          <Segmented
            value={head}
            onChange={(val) => setHead(String(val))}
            options={[
              ...LIVE_MACHINERY_HEADS.map((h) => ({
                label: h.label,
                value: h.key,
              })),
              // Display-only segment — see EQUIPMENT_SEGMENT note above.
              { label: "Equipment", value: EQUIPMENT_SEGMENT },
            ]}
          />
        </div>
        <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
          {head === EQUIPMENT_SEGMENT
            ? "Equipment applies this project's equipment norms (machine crews) — a separate master, not a machinery-norms head."
            : "Norm Codes in the file are matched under this head."}
        </div>
      </div>
    );

    if (head === EQUIPMENT_SEGMENT) {
      // Deployment-norm variant — same modal title, everything else swaps.
      return {
        title: "Apply Machinery (Excel)",
        entityLabel: "Apply Equipment Norms",
        templateSource: { kind: "client", generate: generateApplyDeploymentTemplate },
        startUpload: (formData) => deploymentNormApis.uploadApplyExcel(formData),
        // NO category — deployment norms are not a machinery_norms head.
        buildBody: (comments) => ({
          projectId: Number(projectId),
          comment: comments ? comments : "N/A",
        }),
        introSection: headPicker,
        previewColumns: deploymentPreviewColumns,
        bucketRows: deploymentBucketRows,
        rowErrorLabel: (row: any) =>
          row.excelRow
            ? `Row ${row.excelRow}`
            : row.taskRef
            ? `Task ${row.taskRef}`
            : "Row",
        copy: {
          uploadIntro: (
            <>
              <p>
                Apply equipment norms (machine crews) to many tasks at once.
                Fill the sheet (<b>TaskID</b>, <b>Norm Code</b>,{" "}
                <b>OverrideQty</b>) for tasks in this project, then upload
                below. You'll see a per-row preview before anything is applied
                — valid rows apply, invalid rows are skipped and reported.
              </p>
              <p>
                The Norm Code must be an <b>Active equipment norm of this
                project</b> — org-master norms must be duplicated in first
                (Norms &rsaquo; Machinery &rsaquo; Equipment Norms &rsaquo;
                Duplicate to Project). Blank OverrideQty = task work quantity;
                0 is allowed. Only the quantity freezes at commit — equipment
                hours and amounts stay live-derived from the norm version in
                force and the equipment rates.
              </p>
            </>
          ),
          validatingHeading: "Validating your file…",
          validatingSub:
            "Resolving each TaskID within this project and matching the Norm Code against the project's active equipment norms. This normally takes a few seconds.",
          committingHeading: "Applying equipment norms…",
          committingSub:
            "Applying each norm to its task with the frozen quantity. Valid rows apply; invalid rows are skipped.",
          commitCta: "Apply Equipment Norms",
          doneHeading: "Equipment norms applied",
          doneSub: (report: UploadReportState) => (
            <>
              {report.successCount} row(s) applied
              {report.failureCount ? `, ${report.failureCount} skipped` : ""}.
              Each task's equipment requirement now reflects its applied norms —
              hours and amounts stay live-derived from the version in force.
            </>
          ),
          creationFailedNote:
            "Some rows could not be applied — see the errors in the preview.",
        },
        discardOnCancelFromPreview: false,
      };
    }

    // Machinery-norms variant (unchanged behavior).
    return {
      title: "Apply Machinery (Excel)",
      entityLabel: "Apply Machinery",
      templateSource: { kind: "client", generate: generateApplyMachineryTemplate },
      startUpload: (formData) => machineryApis.applyMachineryExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
        category: head,
      }),
      introSection: headPicker,
      previewColumns: machineryPreviewColumns,
      rowErrorLabel: (row: any) => (row.TaskID ? `Task ${row.TaskID}` : "Row"),
      copy: {
        uploadIntro: (
          <>
            <p>
              Apply machinery norms to many tasks at once. Fill the sheet
              (<b>TaskID</b>, <b>Activity Name</b>, <b>Norm Code</b>,{" "}
              <b>Description</b>, <b>OverrideQty</b>) for tasks in this
              project, then upload below.
              You'll see a per-row preview before anything is applied.
            </p>
            <p>
              Activity Name must be the task's own activity (it is
              cross-checked against the TaskID). Norm Code is matched within
              that activity under the selected Head; add Description only
              when the same code appears
              with multiple descriptions (e.g. Class II / Class III). Blank
              OverrideQty = task work quantity.
            </p>
          </>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Resolving each TaskID, checking the Activity Name, and matching the Norm Code within the task's activity. This normally takes a few seconds.",
        committingHeading: "Applying machinery…",
        committingSub:
          "Applying each norm to its task. Valid rows apply; invalid rows are skipped.",
        commitCta: "Apply Machinery",
        doneHeading: "Machinery applied",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} row(s) applied
            {report.failureCount ? `, ${report.failureCount} skipped` : ""}. Each
            task's "Machinery from Norm" now reflects its applied norms.
          </>
        ),
        creationFailedNote:
          "Some rows could not be applied — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    };
  }, [projectId, head]);

  return (
    // key={head}: switching the head REMOUNTS the staged modal, resetting any
    // in-flight stage (file, preview, polling) cleanly — the two flows poll
    // different report types, so carrying a stage across the switch would mix
    // them up.
    <ExcelUploadStageModal
      key={head}
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default ApplyMachineryNormUploadModal;
